/**
 * Enterprise Theme Configuration
 * Professional MUI theme for industry-grade ERP systems
 * Follows Material Design 3 principles with enterprise enhancements
 */

import { createTheme, ThemeOptions, alpha } from '@mui/material/styles';
import { designTokens } from './tokens';

// Custom module augmentation for theme extensions
declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    uiText: React.CSSProperties;
    uiLabel: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    sectionTitle?: React.CSSProperties;
    uiText?: React.CSSProperties;
    uiLabel?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    sectionTitle: true;
    uiText: true;
    uiLabel: true;
  }
}
//common theme set for save button
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primaryGradient: true;
    resetAction: true;
    cancelAction: true;
    skipAction: true;
    backAction: true;
    approveAction: true;
    rejectAction: true;
    reviewAction: true;
    closeAction: true;
    close: true;
    printAction: true;
    print: true;
    downloadAction: true;
    download: true;
  }
}
const getThemeConfig = (
  mode: 'light' | 'dark',
  fontSize: string = 'medium'
): ThemeOptions => {
  const isDark = mode === 'dark';
  const tokens = designTokens;

  const sizeMap: Record<string, number> = {
    small: 14,
    medium: 16,
    large: 20,
    xl: 24,
    xxl: 28,
  };
  const htmlFontSize = sizeMap[fontSize] || 16;

  return {
    palette: {
      mode,
      primary: {
        main: tokens.colors.primary[500], // Emerald green - professional ERP brand
        light: tokens.colors.primary[400],
        dark: tokens.colors.primary[700],
        contrastText: '#fff',
      },
      secondary: {
        main: tokens.colors.secondary[600], // Teal accent color
        light: tokens.colors.secondary[400],
        dark: tokens.colors.secondary[700],
        contrastText: '#fff',
      },
      tertiary: {
        main: tokens.colors.tertiary[500], // Dark navy for backgrounds
        light: tokens.colors.tertiary[400],
        dark: tokens.colors.tertiary[800],
        contrastText: '#fff',
      },
      error: {
        main: tokens.colors.error[500],
        light: tokens.colors.error[100],
        dark: tokens.colors.error[700],
      },
      warning: {
        main: tokens.colors.warning[500],
        light: tokens.colors.warning[100],
        dark: tokens.colors.warning[700],
      },
      info: {
        main: tokens.colors.info[500],
        light: tokens.colors.info[100],
        dark: tokens.colors.info[700],
      },
      success: {
        main: tokens.colors.success[500],
        light: tokens.colors.success[100],
        dark: tokens.colors.success[700],
      },
      grey: tokens.colors.neutral,
      background: {
        default: isDark
          ? tokens.colors.background.dark
          : tokens.colors.background.default,
        paper: isDark
          ? tokens.colors.background.darkPaper
          : tokens.colors.background.paper,
      },
      text: {
        primary: isDark
          ? tokens.colors.text.primaryDark
          : tokens.colors.text.primary,
        secondary: isDark
          ? tokens.colors.text.secondaryDark
          : tokens.colors.text.secondary,
        disabled: isDark
          ? tokens.colors.text.disabledDark
          : tokens.colors.text.disabled,
      },
      divider: isDark ? tokens.colors.neutral[700] : tokens.colors.neutral[300],
    },

    typography: {
      fontFamily: tokens.typography.fontFamily.primary,
      htmlFontSize,
      fontWeightLight: tokens.typography.fontWeight.light,
      fontWeightRegular: tokens.typography.fontWeight.regular,
      fontWeightMedium: tokens.typography.fontWeight.medium,
      fontWeightBold: tokens.typography.fontWeight.bold,

      // Main headings — descending scale
      h1: {
        fontSize: tokens.typography.fontSize['3xl'],
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: tokens.typography.letterSpacing.tight,
      },
      h2: {
        fontSize: tokens.typography.fontSize['2xl'],
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
        letterSpacing: tokens.typography.letterSpacing.tight,
      },
      h3: {
        fontSize: tokens.typography.fontSize.xl,
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h4: {
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h5: {
        fontSize: tokens.typography.fontSize.base,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      h6: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },

      // Body
      body1: {
        fontSize: tokens.typography.fontSize.base,
        fontWeight: tokens.typography.fontWeight.regular,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      body2: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.regular,
        lineHeight: tokens.typography.lineHeight.normal,
      },

      // Subheadings
      subtitle1: {
        fontSize: tokens.typography.fontSize.base,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      subtitle2: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.medium,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      caption: {
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.regular,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      overline: {
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.medium,
        textTransform: 'uppercase',
        letterSpacing: tokens.typography.letterSpacing.wider,
      },
      button: {
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.medium,
        textTransform: 'none',
        letterSpacing: tokens.typography.letterSpacing.wide,
      },

      // Custom variants for recurring UI patterns
      pageTitle: {
        fontSize: '1.375rem',
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      sectionTitle: {
        fontSize: tokens.typography.fontSize.ui,
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      uiText: {
        fontSize: tokens.typography.fontSize.ui,
        fontWeight: tokens.typography.fontWeight.regular,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      uiLabel: {
        fontSize: tokens.typography.fontSize['2xs'],
        fontWeight: tokens.typography.fontWeight.semibold,
        lineHeight: tokens.typography.lineHeight.normal,
        textTransform: 'uppercase',
        letterSpacing: tokens.typography.letterSpacing.wider,
      },
    },

    shape: {
      borderRadius: parseInt(tokens.borderRadius.lg),
    },

    spacing: 8, // Base spacing unit (8px grid)

    shadows: [
      'none',
      tokens.shadows.elevation[1],
      tokens.shadows.elevation[2],
      tokens.shadows.elevation[3],
      tokens.shadows.elevation[4],
      tokens.shadows.elevation[4],
      tokens.shadows.elevation[6],
      tokens.shadows.elevation[6],
      tokens.shadows.elevation[8],
      tokens.shadows.elevation[8],
      tokens.shadows.elevation[8],
      tokens.shadows.elevation[8],
      tokens.shadows.elevation[12],
      tokens.shadows.elevation[12],
      tokens.shadows.elevation[12],
      tokens.shadows.elevation[12],
      tokens.shadows.elevation[16],
      tokens.shadows.elevation[16],
      tokens.shadows.elevation[16],
      tokens.shadows.elevation[16],
      tokens.shadows.elevation[24],
      tokens.shadows.elevation[24],
      tokens.shadows.elevation[24],
      tokens.shadows.elevation[24],
      tokens.shadows.elevation[24],
    ],

    transitions: {
      duration: {
        shortest: parseInt(tokens.transitions.duration.fastest),
        shorter: parseInt(tokens.transitions.duration.fast),
        short: parseInt(tokens.transitions.duration.normal),
        standard: parseInt(tokens.transitions.duration.normal),
        complex: parseInt(tokens.transitions.duration.slow),
        enteringScreen: parseInt(tokens.transitions.duration.normal),
        leavingScreen: parseInt(tokens.transitions.duration.fast),
      },
      easing: {
        easeInOut: tokens.transitions.timing.easeInOut,
        easeOut: tokens.transitions.timing.easeOut,
        easeIn: tokens.transitions.timing.easeIn,
        sharp: tokens.transitions.timing.sharp,
      },
    },

    breakpoints: {
      values: tokens.breakpoints,
    },

    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: 1100,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },

    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
          },
        },
      },

      // ============================================
      // BUTTON - Professional Button Styles
      // ============================================
      MuiButton: {
        defaultProps: {
          disableElevation: false,
        },
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.lg,
            textTransform: 'none',
            fontFamily: tokens.typography.fontFamily.primary,
            fontWeight: tokens.typography.fontWeight.medium,
            padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
            transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,
          },
          sizeLarge: {
            padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
            fontSize: tokens.typography.fontSize.base,
          },
          sizeMedium: {
            padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
          },
          sizeSmall: {
            padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
            fontSize: tokens.typography.fontSize.xs,
          },
          contained: {
            boxShadow: tokens.shadows.elevation[2],
            '&:hover': {
              boxShadow: tokens.shadows.elevation[4],
            },
            '&:active': {
              boxShadow: tokens.shadows.elevation[1],
            },
          },
        },
        variants: [
          {
            props: { variant: 'backAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#fff',
              color: '#64748b',
              border: '1.5px solid #e2e8f0',
              boxShadow: 'none',

              '&:hover': {
                backgroundColor: '#f1f5f9',
                borderColor: '#cbd5e1',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#cbd5e1',
                borderColor: '#f1f5f9',
                backgroundColor: '#fff',
              },
            },
          },
          {
            props: { variant: 'cancelAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#fff',
              color: '#ef4444',
              border: '1.5px solid #fca5a5',
              boxShadow: 'none',

              '&:hover': {
                backgroundColor: '#fef2f2',
                borderColor: '#ef4444',
                boxShadow: 'none',
              },
            },
          },

          {
            props: { variant: 'skipAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#fff',
              color: '#64748b',
              border: '1.5px solid #e2e8f0',
              boxShadow: 'none',

              '&:hover': {
                backgroundColor: '#f1f5f9',
                borderColor: '#cbd5e1',
                boxShadow: 'none',
              },

              '&.Mui-disabled': {
                color: '#cbd5e1',
                borderColor: '#f1f5f9',
              },
            },
          },
          {
            props: { variant: 'resetAction' as any },
            style: {
              color: '#475569',
              backgroundColor: '#FFFFFF',
              border: '1px solid #CBD5E1',
              fontWeight: tokens.typography.fontWeight.semibold,
              borderRadius: '10px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',

              '&:hover': {
                backgroundColor: '#F8FAFC',
                borderColor: '#94A3B8',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)',
              },
            },
          },
          {
            props: { variant: 'primaryGradient' as any },
            style: {
              color: '#fff',
              fontWeight: tokens.typography.fontWeight.semibold,
              borderRadius: '10px',
              padding: '8px 24px',
              backgroundImage:
                'linear-gradient(135deg, #1ABC55 0%, #15A349 100%)',

              '&:hover': {
                backgroundImage:
                  'linear-gradient(135deg, #15A349 0%, #118A3E 100%)',
                boxShadow: '0 4px 12px rgba(26,188,85,0.35)',
                transform: 'translateY(-1px)',
              },

              '&.Mui-disabled': {
                color: '#ffffff',
                opacity: 0.55,
                backgroundImage: 'none',
                backgroundColor: '#94a3b8',
                boxShadow: 'none',
                cursor: 'not-allowed',
              },
            },
          },
          {
            props: { variant: 'approveAction' as any },
            style: {
              backgroundColor: '#E1F0E6',
              color: '#245C3E',
              boxShadow: 'none',
              fontWeight: tokens.typography.fontWeight.semibold,
              fontSize: tokens.typography.fontSize.xs,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#cfe8d7', boxShadow: 'none' },
            },
          },
          {
            props: { variant: 'reviewAction' as any },
            style: {
              backgroundColor: '#E4EFF8',
              color: '#1E5A8C',
              boxShadow: 'none',
              fontWeight: tokens.typography.fontWeight.semibold,
              fontSize: tokens.typography.fontSize.xs,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#d3e6f4', boxShadow: 'none' },
            },
          },
          {
            props: { variant: 'rejectAction' as any },
            style: {
              backgroundColor: '#F8E4E4',
              color: '#8C2E2E',
              boxShadow: 'none',
              fontWeight: tokens.typography.fontWeight.semibold,
              fontSize: tokens.typography.fontSize.xs,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#f2d2d2', boxShadow: 'none' },
            },
          },
          {
            props: { variant: 'closeAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#475569',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F8FAFC',
                borderColor: '#94A3B8',
                color: '#1E293B',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
          {
            props: { variant: 'close' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#475569',
              border: '1.5px solid #CBD5E1',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F8FAFC',
                borderColor: '#94A3B8',
                color: '#1E293B',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
          {
            props: { variant: 'printAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#0284C7',
              border: '1.5px solid #7DD3FC',
              boxShadow: '0 1px 2px rgba(2, 132, 199, 0.08)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F0F9FF',
                borderColor: '#0284C7',
                color: '#0369A1',
                boxShadow: '0 3px 8px rgba(2, 132, 199, 0.18)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
          {
            props: { variant: 'print' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#0284C7',
              border: '1.5px solid #7DD3FC',
              boxShadow: '0 1px 2px rgba(2, 132, 199, 0.08)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F0F9FF',
                borderColor: '#0284C7',
                color: '#0369A1',
                boxShadow: '0 3px 8px rgba(2, 132, 199, 0.18)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
          {
            props: { variant: 'downloadAction' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#0D6F5D',
              border: '1.5px solid #6EE7B7',
              boxShadow: '0 1px 2px rgba(13, 111, 93, 0.08)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F0FDF4',
                borderColor: '#0D6F5D',
                color: '#0A5648',
                boxShadow: '0 3px 8px rgba(13, 111, 93, 0.18)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
          {
            props: { variant: 'download' as any },
            style: {
              fontSize: tokens.typography.fontSize.ui,
              fontWeight: tokens.typography.fontWeight.semibold,
              textTransform: 'none',
              borderRadius: '10px',
              padding: '8px 20px',
              backgroundColor: '#FFFFFF',
              color: '#0D6F5D',
              border: '1.5px solid #6EE7B7',
              boxShadow: '0 1px 2px rgba(13, 111, 93, 0.08)',
              transition: `all ${tokens.transitions.duration.fast} ${tokens.transitions.timing.easeInOut}`,

              '&:hover': {
                backgroundColor: '#F0FDF4',
                borderColor: '#0D6F5D',
                color: '#0A5648',
                boxShadow: '0 3px 8px rgba(13, 111, 93, 0.18)',
                transform: 'translateY(-1px)',
              },

              '&:active': {
                transform: 'translateY(0)',
              },

              '&.Mui-disabled': {
                color: '#CBD5E1',
                borderColor: '#F1F5F9',
                backgroundColor: '#FFFFFF',
              },
            },
          },
        ],
      },

      // ============================================
      // CARD - Elevated Card Styles
      // ============================================
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.xl,
            border: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
            boxShadow: tokens.shadows.elevation[1],
            transition: `all ${tokens.transitions.duration.normal} ${tokens.transitions.timing.easeInOut}`,
            '&:hover': {
              boxShadow: tokens.shadows.elevation[3],
            },
          },
        },
      },

      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: tokens.spacing[6],
          },
          title: {
            fontSize: tokens.typography.fontSize.lg,
            fontWeight: tokens.typography.fontWeight.semibold,
          },
        },
      },

      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: tokens.spacing[6],
            '&:last-child': {
              paddingBottom: tokens.spacing[6],
            },
          },
        },
      },

      // ============================================
      // PAPER - Surface Styles
      // ============================================
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: tokens.borderRadius.xl,
          },
          outlined: {
            border: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
        },
      },

      // ============================================
      // TEXT FIELD - Professional Input Fields
      // ============================================
      // MuiTextField: {
      //   defaultProps: {
      //     variant: 'outlined',
      //   },
      // },

      // MuiOutlinedInput: {
      //   styleOverrides: {
      //     root: {
      //       borderRadius: tokens.borderRadius.lg,
      //       '& .MuiOutlinedInput-notchedOutline': {
      //         borderColor: isDark
      //           ? tokens.colors.neutral[700]
      //           : tokens.colors.neutral[300],
      //       },
      //       '&:hover .MuiOutlinedInput-notchedOutline': {
      //         borderColor: isDark
      //           ? tokens.colors.neutral[600]
      //           : tokens.colors.neutral[400],
      //       },
      //       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      //         borderWidth: 2,
      //       },
      //     },
      //     input: {
      //       padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
      //     },
      //   },
      // },

      // MuiInputLabel: {
      //   defaultProps: {
      //     shrink: true,
      //   },
      //   styleOverrides: {
      //     outlined: {
      //       transform: 'translate(14px, -9px) scale(0.75)',
      //       backgroundColor: isDark
      //         ? tokens.colors.background.darkPaper
      //         : tokens.colors.background.paper,
      //       padding: '0 8px',
      //     },
      //   },
      // },
      // ============================================
      // TEXT FIELD - Common Style For Entire App
      // ============================================

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
          fullWidth: true,
        },
      },

      MuiAutocomplete: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          inputRoot: {
            minHeight: 'unset', // let MuiOutlinedInput control height

            '& .MuiAutocomplete-input': {
              padding: 'inherit', // inherit from MuiOutlinedInput.input
            },
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: tokens.typography.fontFamily.primary,
          },
        },
      },

      MuiListItemText: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            fontSize: tokens.typography.fontSize.ui,
            color: '#1e293b',
            borderRadius: '10px',
            // backgroundColor: '#f8fafc',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0',
              borderWidth: '0.5px',
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#cbd5e1',
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3B82F6',
              borderWidth: '1.5px',
            },

            '&.Mui-disabled': {
              backgroundColor: '#f8fafc',

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#f1f5f9',
              },
            },
          },

          input: {
            // padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,

            '&.Mui-disabled': {
              WebkitTextFillColor: '#64748b',
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            fontSize: tokens.typography.fontSize.ui,
            color: '#94a3b8',

            '&.Mui-focused': {
              color: '#3B82F6',
            },

            '&.Mui-disabled': {
              color: '#cbd5e1',
            },
          },

          asterisk: {
            color: '#E24B4A',
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            fontSize: tokens.typography.fontSize['2xs'],
            marginLeft: '4px',
          },
        },
      },

      // ============================================
      // CHIP - Status & Tag Styles
      // ============================================
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.md,
            fontWeight: tokens.typography.fontWeight.medium,
          },
          filled: {
            backgroundColor: isDark
              ? tokens.colors.neutral[800]
              : tokens.colors.neutral[100],
          },
        },
      },

      // ============================================
      // TABLE - Professional Data Table
      // ============================================
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.xl,
            border: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? tokens.colors.neutral[900]
              : tokens.colors.neutral[50],
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          head: {
            fontFamily: tokens.typography.fontFamily.primary,
            fontWeight: tokens.typography.fontWeight.semibold,
            fontSize: tokens.typography.fontSize.sm,
            textTransform: 'uppercase',
            letterSpacing: tokens.typography.letterSpacing.wide,
            color: isDark
              ? tokens.colors.text.secondaryDark
              : tokens.colors.text.secondary,
            borderBottom: `2px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            borderBottom: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark
                ? alpha(tokens.colors.primary[900], 0.08)
                : alpha(tokens.colors.primary[50], 0.5),
            },
            '&:last-child td': {
              borderBottom: 0,
            },
          },
        },
      },

      // ============================================
      // DIALOG - Modal Styles
      // ============================================
      MuiDialog: {
        styleOverrides: {
          // phones (< sm): every dialog is a bottom sheet — full width, as tall as its content up to
          // the screen height, clear of the home indicator. Full-screen dialogs are left as they are.
          container: {
            [`@media (max-width:${tokens.breakpoints.sm - 0.05}px)`]: {
              alignItems: 'flex-end',
            },
          },
          paper: {
            borderRadius: tokens.borderRadius['2xl'],
            boxShadow: tokens.shadows.elevation[24],
            [`@media (max-width:${tokens.breakpoints.sm - 0.05}px)`]: {
              '&:not(.MuiDialog-paperFullScreen)': {
                margin: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 24px)',
                '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 24px)' },
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                paddingBottom: 'env(safe-area-inset-bottom)',
              },
            },
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            fontSize: tokens.typography.fontSize.xl,
            fontWeight: tokens.typography.fontWeight.semibold,
            padding: tokens.spacing[6],
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            padding: tokens.spacing[6],
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            fontFamily: tokens.typography.fontFamily.primary,
            padding: tokens.spacing[6],
            gap: tokens.spacing[3],
          },
        },
      },

      // ============================================
      // APP BAR - Header Styles
      // ============================================
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? tokens.colors.background.darkPaper
              : tokens.colors.background.paper,
            color: isDark
              ? tokens.colors.text.primaryDark
              : tokens.colors.text.primary,
            borderBottom: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
        },
      },

      // ============================================
      // DRAWER - Sidebar Styles
      // ============================================
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
            backgroundColor: isDark
              ? tokens.colors.background.darkPaper
              : tokens.colors.background.paper,
          },
        },
      },

      // ============================================
      // TOOLTIP - Enhanced Tooltips
      // ============================================
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: tokens.typography.fontFamily.primary,
            backgroundColor: isDark
              ? tokens.colors.neutral[800]
              : tokens.colors.neutral[700],
            fontSize: tokens.typography.fontSize.xs,
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            borderRadius: tokens.borderRadius.md,
          },
          arrow: {
            color: isDark
              ? tokens.colors.neutral[800]
              : tokens.colors.neutral[700],
          },
        },
      },

      // ============================================
      // ALERT - Notification Styles
      // ============================================
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.lg,
            padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
          },
          standardSuccess: {
            backgroundColor: isDark
              ? alpha(tokens.colors.success[500], 0.15)
              : tokens.colors.success[50],
            color: isDark
              ? tokens.colors.success[100]
              : tokens.colors.success[900],
          },
          standardError: {
            backgroundColor: isDark
              ? alpha(tokens.colors.error[500], 0.15)
              : tokens.colors.error[50],
            color: isDark ? tokens.colors.error[100] : tokens.colors.error[900],
          },
          standardWarning: {
            backgroundColor: isDark
              ? alpha(tokens.colors.warning[500], 0.15)
              : tokens.colors.warning[50],
            color: isDark
              ? tokens.colors.warning[100]
              : tokens.colors.warning[900],
          },
          standardInfo: {
            backgroundColor: isDark
              ? alpha(tokens.colors.info[500], 0.15)
              : tokens.colors.info[50],
            color: isDark ? tokens.colors.info[100] : tokens.colors.info[900],
          },
        },
      },

      // ============================================
      // BREADCRUMBS - Navigation Styles
      // ============================================
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: {
            marginLeft: tokens.spacing[2],
            marginRight: tokens.spacing[2],
          },
        },
      },

      // ============================================
      // TABS - Tab Navigation
      // ============================================
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${
              isDark ? tokens.colors.neutral[800] : tokens.colors.neutral[200]
            }`,
          },
          indicator: {
            height: 3,
            borderRadius: `${tokens.borderRadius.full} ${tokens.borderRadius.full} 0 0`,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: tokens.typography.fontWeight.medium,
            fontSize: tokens.typography.fontSize.sm,
            minHeight: 48,
            padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
          },
        },
      },

      // ============================================
      // MENU - Dropdown Menus
      // ============================================
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.borderRadius.lg,
            marginTop: tokens.spacing[1],
            boxShadow: tokens.shadows.elevation[8],
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
            borderRadius: tokens.borderRadius.base,
            margin: `0 ${tokens.spacing[1]}`,
            '&:hover': {
              backgroundColor: isDark
                ? alpha(tokens.colors.primary[500], 0.12)
                : alpha(tokens.colors.primary[500], 0.08),
            },
          },
        },
      },
    },
  };
};

export const createErpTheme = (
  mode: 'light' | 'dark' = 'light',
  fontSize: string = 'medium'
) => {
  return createTheme(getThemeConfig(mode, fontSize));
};

export const lightTheme = createErpTheme('light');
export const darkTheme = createErpTheme('dark');

export default createErpTheme;
